import { useState, useEffect } from 'react';
import { AlertTriangle, Activity, CheckCircle, Eye } from 'lucide-react';
import ModuleContainer from '../layout/ModuleContainer.jsx';
import InfoButton from '../common/InfoButton.jsx';

const STATE_CONFIG = {
  MONITORING:     { icon: Eye,           color: '#6b7280', label: 'Monitoring',     desc: 'Watching indicators', action: 'No action — normal market conditions. System tracking fear metrics.' },
  ALL_FEAR:       { icon: AlertTriangle, color: '#ef4444', label: 'All Fear',       desc: 'All indicators extreme', action: 'DO NOT BUY YET. All fear indicators are at extreme levels. Wait for the turn.' },
  TURN_DETECTED:  { icon: Activity,      color: '#f59e0b', label: 'Turn Detected',  desc: 'One indicator improving', action: 'PREPARE. One indicator is turning while others remain fearful. The "don\'t be too early" mechanism is active — wait for 2+ to confirm.' },
  CONFIRMED:      { icon: CheckCircle,   color: '#10b981', label: 'Confirmed',      desc: 'Capitulation signal active', action: 'CONSIDER CONTRARIAN ENTRIES with defined risk. Fear peaked and is turning — this is the historically optimal entry window.' },
};

function FearGauge({ value }) {
  const angle = value != null ? (value / 100) * 180 - 90 : -90;
  const color = value <= 25 ? '#ef4444' : value <= 45 ? '#f59e0b' : value <= 55 ? '#6b7280' : value <= 75 ? '#10b981' : '#22c55e';

  return (
    <div className="relative w-40 h-20 mx-auto">
      <svg viewBox="0 0 200 110" className="w-full h-full">
        {/* Background arc */}
        <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="#1f2937" strokeWidth="12" strokeLinecap="round" />
        {/* Colored arc */}
        {value != null && (
          <path
            d="M 20 100 A 80 80 0 0 1 180 100"
            fill="none" stroke={color} strokeWidth="12" strokeLinecap="round"
            strokeDasharray={`${(value / 100) * 251} 251`}
          />
        )}
        {/* Needle */}
        <line
          x1="100" y1="100" x2={100 + 60 * Math.cos(angle * Math.PI / 180)} y2={100 + 60 * Math.sin(angle * Math.PI / 180)}
          stroke="white" strokeWidth="2" strokeLinecap="round"
        />
        <circle cx="100" cy="100" r="4" fill="white" />
        {/* Labels */}
        <text x="15" y="108" className="text-[10px]" fill="#6b7280" fontFamily="monospace">Fear</text>
        <text x="155" y="108" className="text-[10px]" fill="#6b7280" fontFamily="monospace">Greed</text>
      </svg>
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-center">
        <span className="text-2xl font-mono font-bold text-white">{value ?? '—'}</span>
      </div>
    </div>
  );
}

export default function CapitulationDetector() {
  const [data, setData] = useState(null);

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await fetch('/api/signals/capitulation');
        if (res.ok) setData(await res.json());
      } catch { /* silent */ }
    }
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  const state = data?.currentState?.state || 'MONITORING';
  const config = STATE_CONFIG[state];
  const StateIcon = config.icon;
  const latestFG = data?.fearGreedHistory?.[0];
  const indicators = data?.currentState?.indicators;

  return (
    <ModuleContainer title="Capitulation Detector" subtitle="Fear indicators + capitulation state machine">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left: Fear & Greed Gauge */}
        <div>
          <FearGauge value={latestFG?.index_value ?? null} />
          <p className="text-center text-xs font-mono text-korinth-muted mt-2">
            {latestFG?.classification || 'Awaiting data'}
          </p>

          {/* Sparkline (simplified bar chart) */}
          {data?.fearGreedHistory?.length > 0 && (
            <div className="flex items-end gap-px mt-3 h-8 justify-center">
              {data.fearGreedHistory.slice(0, 20).reverse().map((fg, i) => (
                <div
                  key={i}
                  className="w-1.5 rounded-t"
                  style={{
                    height: `${(fg.index_value / 100) * 100}%`,
                    backgroundColor: fg.index_value <= 25 ? '#ef4444' : fg.index_value <= 50 ? '#f59e0b' : '#10b981',
                    opacity: 0.4 + (i / 20) * 0.6,
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Right: State Machine */}
        <div>
          <h4 className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider mb-3">
            Capitulation State
            <InfoButton title="Capitulation State Machine">
              <p><b>MONITORING</b> — Normal. Tracking Fear &amp; Greed, SPX drawdown, BTC drawdown.</p>
              <p><b>ALL FEAR</b> — All indicators hit extreme levels simultaneously. DON'T buy yet — this is the panic, not the bottom.</p>
              <p><b>TURN DETECTED</b> — One indicator starts improving while others are still fearful. The system is watching for confirmation.</p>
              <p><b>CONFIRMED</b> — 2+ indicators have improved. This is the capitulation signal — the "don't be too early" mechanism has triggered. Historical win rate for contrarian entries here is high.</p>
            </InfoButton>
          </h4>

          {/* State boxes */}
          <div className="space-y-2">
            {Object.entries(STATE_CONFIG).map(([key, cfg]) => {
              const isActive = key === state;
              const Icon = cfg.icon;
              return (
                <div
                  key={key}
                  className={`flex items-center gap-3 px-3 py-2 rounded border transition-all ${
                    isActive
                      ? 'border-opacity-50 bg-opacity-10'
                      : 'border-korinth-border/30 opacity-40'
                  }`}
                  style={isActive ? { borderColor: cfg.color, backgroundColor: cfg.color + '15' } : {}}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" style={{ color: isActive ? cfg.color : '#374151' }} />
                  <div className="flex-1">
                    <span className="text-xs font-mono font-bold" style={{ color: isActive ? cfg.color : '#6b7280' }}>{cfg.label}</span>
                    <p className="text-[9px] text-korinth-muted">{cfg.desc}</p>
                    {isActive && <p className="text-[9px] font-mono mt-0.5" style={{ color: cfg.color }}>{cfg.action}</p>}
                  </div>
                  {isActive && <div className="w-1.5 h-1.5 rounded-full animate-pulse ml-auto flex-shrink-0" style={{ backgroundColor: cfg.color }} />}
                </div>
              );
            })}
          </div>

          {/* Turn indicator detail */}
          {state === 'TURN_DETECTED' && data?.currentState?.turn_indicator && (
            <div className="mt-3 p-2 bg-korinth-warning/10 rounded border border-korinth-warning/20">
              <p className="text-[10px] font-mono text-korinth-warning">
                Turn: {data.currentState.turn_indicator} improving to {data.currentState.turn_value}
              </p>
            </div>
          )}

          {state === 'CONFIRMED' && (
            <div className="mt-3 p-3 bg-korinth-accent/10 rounded border border-korinth-accent/30 animate-pulse">
              <p className="text-xs font-mono font-bold text-korinth-accent text-center">
                CAPITULATION SIGNAL ACTIVE
              </p>
              <p className="text-[10px] font-mono text-korinth-muted text-center mt-1">
                Consider contrarian entries with defined risk
              </p>
            </div>
          )}
        </div>
      </div>
    </ModuleContainer>
  );
}
